import { useEffect, useState } from 'react';
import {
    useRecordContext,
    useResourceContext,
    useTranslate,
    useNotify,
    useRefresh,
    useGetIdentity,
} from 'react-admin';
import { useCanAccess } from '@react-admin/ra-rbac';
import {
    Stack,
    Typography,
    Button,
    Tooltip,
    CircularProgress,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
} from '@mui/material';

//Järjestelmänvalvoja (app:400+) ei ole sidottu peruutusikkunan aikarajaan, sama sääntö kuin backendissä
const CANCEL_DEADLINE_BYPASS_USERRIGHTS = 400;

type MergeEntry = {
    guid: string;
    from: { guid: string; email?: string; username?: string };
    created: string;
    materials_merged: boolean;
    materials_merged_at: string | null;
    cancellable: boolean;
    cancel_deadline: string;
};

const ConditionalMergeField = () => {
    const record = useRecordContext();
    const resource = useResourceContext();
    const translate = useTranslate();
    const notify = useNotify();
    const refresh = useRefresh();
    const { identity } = useGetIdentity();
    const { canAccess: canManageMerges } = useCanAccess({
        action: 'read',
        resource: 'merge',
    });
    const { canAccess: canCancelMerge } = useCanAccess({
        action: 'cancel_merge',
        resource,
    });

    const [merges, setMerges] = useState<MergeEntry[]>([]);
    const [loading, setLoading] = useState(false);
    const [pendingCancel, setPendingCancel] = useState<MergeEntry | null>(null);

    const hasMergedAccounts =
        (record?.public?.['infra:merged']?.length ?? 0) > 0;

    const fetchMerges = async () => {
        if (!record?.guid) return;
        setLoading(true);
        try {
            const response = await fetch(
                `${process.env.REACT_APP_INFRA_API}/user/${record.guid}/merge`,
                {
                    headers: {
                        Accept: 'application/json',
                        Authorization:
                            'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );
            if (!response.ok) throw new Error('fetch failed');
            setMerges(await response.json());
        } catch (error) {
            setMerges([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (canManageMerges && hasMergedAccounts) fetchMerges();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [record?.guid, hasMergedAccounts, canManageMerges]);

    const handleCancelMerge = async (merge: MergeEntry) => {
        try {
            const response = await fetch(
                `${process.env.REACT_APP_INFRA_API}/user/merge/${merge.guid}/cancel`,
                {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Accept: 'application/json',
                        Authorization:
                            'Bearer ' + localStorage.getItem('token'),
                    },
                }
            );
            if (!response.ok) throw new Error('cancel failed');

            notify(translate('merge.profilepage.cancel_success'), {
                type: 'success',
            });
            setPendingCancel(null);
            fetchMerges();
            refresh();
        } catch (error) {
            notify(translate('merge.profilepage.cancel_error'), {
                type: 'error',
            });
        }
    };

    //Peilaa backendin aikaraja-ohitusta simuloidulla roolilla (RoleSwitcher), jotta esikatselu vastaa oikeaa käyttäytyä
    const isCancellable = (merge: MergeEntry) => {
        if (merge.materials_merged) return false;
        if ((identity?.userrights ?? 0) >= CANCEL_DEADLINE_BYPASS_USERRIGHTS)
            return true;
        return new Date() < new Date(merge.cancel_deadline);
    };

    if (!record || !hasMergedAccounts || !canManageMerges) return null;

    return (
        <Stack direction="column" spacing={1} sx={{ width: '100%' }}>
            <Typography variant="body2">
                {translate('merge.profilepage.description')}
            </Typography>
            {loading && <CircularProgress size={16} />}
            {merges.map(merge => {
                const cancellable = isCancellable(merge);
                const disabledReason = merge.materials_merged
                    ? translate('merge.profilepage.cancel_materials_merged')
                    : !cancellable
                      ? translate('merge.profilepage.cancel_deadline_passed')
                      : '';
                return (
                    <Stack
                        key={merge.guid}
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        justifyContent="space-between"
                    >
                        <Typography variant="body2">
                            {merge.from.email || merge.from.username}{' '}
                            <Typography
                                component="span"
                                variant="body2"
                                sx={{ opacity: 0.6 }}
                            >
                                (
                                {new Date(merge.created).toLocaleDateString(
                                    'fi-FI'
                                )}
                                )
                            </Typography>
                        </Typography>
                        {canCancelMerge && (
                            <Tooltip title={disabledReason}>
                                <span>
                                    <Button
                                        size="small"
                                        color="error"
                                        variant="outlined"
                                        disabled={!cancellable}
                                        onClick={() => setPendingCancel(merge)}
                                    >
                                        {translate(
                                            'merge.profilepage.cancel_button'
                                        )}
                                    </Button>
                                </span>
                            </Tooltip>
                        )}
                    </Stack>
                );
            })}
            <Dialog
                open={!!pendingCancel}
                onClose={() => setPendingCancel(null)}
            >
                <DialogTitle>
                    {translate('merge.profilepage.cancel_confirm_title')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        {translate('merge.profilepage.cancel_confirm_message')}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => setPendingCancel(null)}
                        variant="outlined"
                    >
                        {translate('merge.cancel')}
                    </Button>
                    <Button
                        onClick={() =>
                            pendingCancel && handleCancelMerge(pendingCancel)
                        }
                        variant="contained"
                        color="error"
                    >
                        {translate('merge.profilepage.cancel_button')}
                    </Button>
                </DialogActions>
            </Dialog>
        </Stack>
    );
};

export default ConditionalMergeField;
